import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../../api/mockData';
import './index.css';

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [showToc, setShowToc] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [activeSection, setActiveSection] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [catMood, setCatMood] = useState('😺');
  const [catFacts, setCatFacts] = useState([
    "猫咪的鼻子纹路就像人类的指纹一样，每只都是独一无二的！",
    "猫咪可以跳到自己身高5倍的高度，真是天生的运动员！",
    "猫咪每天要花14-16小时睡觉，真是睡眠大师！",
    "猫咪的胡须可以帮助它们判断是否能通过狭窄的空间！",
    "猫咪有32块肌肉控制耳朵，可以旋转180度！"
  ]);
  const [randomCatFact, setRandomCatFact] = useState('');
  
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 获取文章详情
        const response = await api.getPost(id);
        if (response.success) {
          setPost(response.data);
          
          // 获取文章评论
          const commentsData = Array.isArray(response.data.comments) 
            ? response.data.comments 
            : response.data.commentsData || [];
          setComments(commentsData);
          
          // 获取相关文章
          if (response.data.category) {
            const relatedResponse = await api.getPosts({ 
              category: response.data.category,
              sort: 'popular' 
            });
            
            if (relatedResponse.success) {
              const filtered = relatedResponse.data
                .filter(p => p.id !== parseInt(id))
                .slice(0, 4);
              setRelatedPosts(filtered);
            }
          }
        }
        
        // 随机猫咪冷知识
        const randomIndex = Math.floor(Math.random() * catFacts.length);
        setRandomCatFact(catFacts[randomIndex]);
      } catch (error) {
        console.error('加载文章失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // 监听滚动
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      
      const scrollTop = window.scrollY;
      const contentTop = contentRef.current.offsetTop - 100;
      const contentHeight = contentRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      
      if (scrollTop >= contentTop) {
        const progress = Math.min(
          ((scrollTop - contentTop) / (contentHeight - windowHeight + 200)) * 100,
          100
        );
        setReadingProgress(progress);
        
        // 根据阅读进度改变猫咪心情
        if (progress < 25) setCatMood('😺');
        else if (progress < 50) setCatMood('😸');
        else if (progress < 75) setCatMood('😻');
        else setCatMood('🎉');
      }
      
      // 更新活动章节
      updateActiveSection();
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 初始调用
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post]);

  // 更新活动章节
  const updateActiveSection = () => {
    if (!contentRef.current) return;
    
    const sections = contentRef.current.querySelectorAll('h2, h3');
    const scrollPosition = window.scrollY + 100;

    let currentSection = null;
    sections.forEach(section => {
      const sectionTop = section.offsetTop;
      if (scrollPosition >= sectionTop) {
        currentSection = section.id || section.textContent;
      }
    });

    setActiveSection(currentSection);
  };

  // 处理点赞
  const handleLike = async () => {
    if (!post) return;
    
    setIsLiked(!isLiked);
    if (!isLiked) {
      try {
        const response = await api.likePost(post.id);
        if (response.success) {
          setPost(prev => ({
            ...prev,
            likes: response.data.likes || prev.likes + 1
          }));
        }
      } catch (error) {
        console.error('点赞失败:', error);
        setIsLiked(false);
      }
    }
  };

  // 处理收藏
  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  // 处理分享
  const handleShare = async () => {
    if (!post) return;
    
    setIsShared(true);
    try {
      if (navigator.share) {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch (error) {
      console.error('分享失败:', error);
    } finally {
      setTimeout(() => setIsShared(false), 2000);
    }
  };

  // 处理评论提交
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !post) return;
    
    try {
      const response = await api.submitComment(post.id, { content: newComment });
      if (response.success && response.data) {
        const newCommentObj = {
          id: response.data.id || Date.now(),
          postId: post.id,
          user: response.data.user || {
            id: 1,
            name: '当前用户',
            avatar: 'https://images.unsplash.com/photo-1533738363-b7f9aef128ce?w=50&h=50&fit=crop&crop=face'
          },
          content: newComment,
          date: new Date().toLocaleDateString('zh-CN') + ' ' + 
                new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
          likes: 0
        };
        
        setComments(prev => [newCommentObj, ...prev]);
        setNewComment('');
        
        // 更新文章评论数
        setPost(prev => ({
          ...prev,
          comments: (prev.comments || 0) + 1
        }));
      }
    } catch (error) {
      console.error('提交评论失败:', error);
    }
  };

  // 处理评论点赞
  const handleCommentLike = (commentId) => {
    setComments(prevComments => 
      prevComments.map(comment => 
        comment.id === commentId 
          ? { ...comment, likes: (comment.likes || 0) + 1 }
          : comment
      )
    );
  };

  // 生成目录
  const generateToc = () => {
    if (!post?.content || !contentRef.current) return [];
    
    const headings = contentRef.current.querySelectorAll('h2, h3');
    const toc = [];
    
    headings.forEach((heading, index) => {
      const id = `section-${index}`;
      heading.id = id;
      toc.push({
        id,
        text: heading.textContent,
        level: heading.tagName,
        index
      });
    });
    
    return toc;
  };

  // 滚动到章节
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setShowToc(false);
    }
  };

  // 获取猫咪表情根据分类
  const getCatEmoji = (category) => {
    const emojiMap = {
      '日常趣事': '😺',
      '养护知识': '📚',
      '行为解读': '🔍',
      '饮食健康': '🍲',
      '新手指南': '🐱',
      '健康护理': '❤️'
    };
    return emojiMap[category] || '🐱';
  };

  // 格式化日期
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    } catch (error) {
      return dateString || '未知日期';
    }
  };

  // 获取新的猫咪冷知识
  const getNewCatFact = () => {
    const newIndex = Math.floor(Math.random() * catFacts.length);
    setRandomCatFact(catFacts[newIndex]);
  };

  if (loading) {
    return (
      <div className="post-detail-loading">
        <div className="loading-container">
          <div className="loading-cat">
            <div className="cat-emoji">🐱</div>
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p className="loading-text">猫咪正在整理文章...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="post-detail-not-found">
        <div className="not-found-container">
          <div className="not-found-cat">😿</div>
          <h2>哎呀，文章不见了！</h2>
          <p>可能猫咪把它藏起来了，或者文章已经被删除了。</p>
          <Link to="/" className="not-found-button">
            <span className="button-icon">🏠</span>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const toc = generateToc();
  const catEmoji = getCatEmoji(post.category);

  return (
    <div className="post-detail-page">
      {/* 阅读进度条 */}
      <div className="reading-progress-container">
        <div 
          className="reading-progress-bar" 
          style={{ width: `${readingProgress}%` }}
        ></div>
        <div className="reading-cat-mood">
          <span className="cat-mood-emoji">{catMood}</span>
          <span className="cat-mood-text">
            {readingProgress < 25 ? '准备阅读...' : 
             readingProgress < 50 ? '认真阅读中...' : 
             readingProgress < 75 ? '快要读完了...' : 
             '阅读完成！'}
          </span>
        </div>
      </div>

      {/* 文章头部 */}
      <header className="post-header">
        <div className="header-container">
          <div className="header-background">
            <div className="header-overlay"></div>
          </div>
          
          <div className="post-metadata">
            {/* 面包屑导航 */}
            <nav className="breadcrumb">
              <Link to="/" className="breadcrumb-item">
                <span className="breadcrumb-icon">🏠</span>
                首页
              </Link>
              <span className="breadcrumb-separator">/</span>
              <Link to="/archive" className="breadcrumb-item">
                <span className="breadcrumb-icon">📚</span>
                文章
              </Link>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-item current">
                {post.title}
              </span>
            </nav>

            {/* 文章分类 */}
            <div className="post-category-tag">
              <span className="category-icon">{catEmoji}</span>
              <span className="category-name">{post.category}</span>
              {post.featured && (
                <span className="featured-badge">
                  <span className="badge-icon">⭐</span>
                  精选
                </span>
              )}
            </div>

            {/* 文章标题 */}
            <h1 className="post-title">{post.title}</h1>
            
            {/* 文章摘要 */}
            <p className="post-excerpt">{post.excerpt}</p>

            {/* 文章元数据 */}
            <div className="post-meta-info">
              <div className="author-info">
                <div className="author-avatar-container">
                  <img 
                    src={post.author?.avatar || 'https://images.unsplash.com/photo-1533738363-b7f9aef128ce?w=100&h=100&fit=crop&crop=face'} 
                    alt={post.author?.name || '作者'}
                    className="author-avatar"
                  />
                  <div className="author-status"></div>
                </div>
                <div className="author-details">
                  <h3 className="author-name">{post.author?.name || '匿名作者'}</h3>
                  <div className="author-stats">
                    <span className="author-stat">
                      <span className="stat-icon">📅</span>
                      {formatDate(post.publishDate)}
                    </span>
                    <span className="author-stat">
                      <span className="stat-icon">👀</span>
                      {post.views || 0} 阅读
                    </span>
                  </div>
                </div>
              </div>

              {/* 文章操作按钮 */}
              <div className="post-actions">
                <button 
                  className={`action-btn like-btn ${isLiked ? 'active' : ''}`}
                  onClick={handleLike}
                >
                  <span className="action-icon">
                    {isLiked ? '❤️' : '🤍'}
                  </span>
                  <span className="action-text">点赞</span>
                  <span className="action-count">{post.likes || 0}</span>
                </button>
                <button 
                  className={`action-btn bookmark-btn ${isBookmarked ? 'active' : ''}`}
                  onClick={handleBookmark}
                >
                  <span className="action-icon">
                    {isBookmarked ? '🔖' : '📑'}
                  </span>
                  <span className="action-text">收藏</span>
                </button>
                <button 
                  className={`action-btn share-btn ${isShared ? 'active' : ''}`}
                  onClick={handleShare}
                >
                  <span className="action-icon">
                    {isShared ? '✅' : '📤'}
                  </span>
                  <span className="action-text">分享</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 主要内容区域 */}
      <main className="post-main-content">
        <div className="content-layout">
          {/* 左侧目录（桌面端） */}
          {toc.length > 0 && (
            <aside className={`toc-sidebar ${showToc ? 'mobile-visible' : ''}`}>
              <div className="toc-header">
                <h3 className="toc-title">
                  <span className="toc-icon">📑</span>
                  目录导航
                </h3>
                <button 
                  className="toc-close"
                  onClick={() => setShowToc(false)}
                >
                  ×
                </button>
              </div>
              <nav className="toc-navigation">
                {toc.map((item, index) => (
                  <a
                    key={index}
                    href={`#${item.id}`}
                    className={`toc-link ${item.level === 'H3' ? 'toc-subitem' : ''} ${
                      activeSection === item.id ? 'active' : ''
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(item.id);
                    }}
                  >
                    <span className="toc-link-icon">
                      {item.level === 'H2' ? '📌' : '📍'}
                    </span>
                    <span className="toc-link-text">{item.text}</span>
                  </a>
                ))}
              </nav>
            </aside>
          )}

          {/* 文章内容 */}
          <article className="post-content" ref={contentRef}>
            {/* 封面图片 */}
            {post.coverImage && (
              <div className="post-cover-image">
                <img 
                  src={post.coverImage} 
                  alt={post.title}
                  className="cover-img"
                />
                <div className="cover-overlay">
                  <div className="cover-cat">🐱</div>
                </div>
              </div>
            )}

            {/* 内容容器 */}
            <div className="content-container">
              {/* 浮动目录按钮（移动端） */}
              {toc.length > 0 && (
                <button 
                  className="floating-toc-btn"
                  onClick={() => setShowToc(true)}
                >
                  <span className="toc-btn-icon">📑</span>
                  <span className="toc-btn-text">目录</span>
                </button>
              )}

              {/* 文章正文 */}
              <div 
                className="article-body"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* 底部操作栏 */}
              <div className="post-footer-minimal">
                <div className="footer-minimal-content">
                  <div className="minimal-stats">
                    <button 
                      className={`minimal-stat-btn like-minimal ${isLiked ? 'active' : ''}`}
                      onClick={handleLike}
                    >
                  <span className="minimal-stat-icon">
                    {isLiked ? '❤️' : '🤍'}
                  </span>
                  <span className="minimal-stat-count">{post.likes || 0}</span>
                  </button>
                  <button 
                    className="minimal-stat-btn comment-minimal"
                    onClick={() => document.querySelector('.comments-section')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <span className="minimal-stat-icon">💬</span>
                    <span className="minimal-stat-count">{comments.length}</span>
                  </button>
                  </div>
    
                   <div className="minimal-actions">
                    <button 
                      className={`minimal-action-btn bookmark-minimal ${isBookmarked ? 'active' : ''}`}
                      onClick={handleBookmark}
                    >
                    <span className="minimal-action-icon">
                       {isBookmarked ? '🔖' : '📖'}
                    </span>
                    </button>
                    <button 
                      className={`minimal-action-btn share-minimal ${isShared ? 'active' : ''}`}
                      onClick={handleShare}
                    >
                      <span className="minimal-action-icon">
                      {isShared ? '✅' : '📤'}
                    </span>
                    </button>
                    </div>
                  </div>
              </div>

              {/* 作者信息卡片 */}
              <div className="author-card-section">
                <div className="author-card">
                  <div className="author-card-header">
                    <img 
                      src={post.author?.avatar || 'https://images.unsplash.com/photo-1533738363-b7f9aef128ce?w=150&h=150&fit=crop&crop=face'} 
                      alt={post.author?.name || '作者'}
                      className="author-card-avatar"
                    />
                    <div className="author-card-info">
                      <h3 className="author-card-name">{post.author?.name || '匿名作者'}</h3>
                      <p className="author-card-bio">
                        {post.author?.bio || '资深猫奴，热爱分享猫咪知识'}
                      </p>
                    </div>
                  </div>
                  <div className="author-card-stats">
                    <div className="author-card-stat">
                      <span className="card-stat-value">{post.likes || 0}</span>
                      <span className="card-stat-label">获赞</span>
                    </div>
                    <div className="author-card-stat">
                      <span className="card-stat-value">{post.views || 0}</span>
                      <span className="card-stat-label">阅读</span>
                    </div>
                    <div className="author-card-stat">
                      <span className="card-stat-value">{comments.length}</span>
                      <span className="card-stat-label">评论</span>
                    </div>
                  </div>
                  <button className="author-follow-btn">
                    <span className="follow-icon">+</span>
                    关注作者
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* 右侧边栏 */}
          <aside className="post-sidebar">
            {/* 猫咪冷知识 */}
            <div className="sidebar-widget cat-fact-widget">
              <div className="widget-header">
                <h3 className="widget-title">
                  <span className="widget-icon">😺</span>
                  猫咪冷知识
                </h3>
                <button 
                  className="widget-refresh"
                  onClick={getNewCatFact}
                >
                  <span className="refresh-icon">🔄</span>
                </button>
              </div>
              <div className="widget-content">
                <div className="cat-fact-emoji">🐱</div>
                <p className="cat-fact-text">{randomCatFact}</p>
              </div>
            </div>

            {/* 相关文章 */}
            {relatedPosts.length > 0 && (
              <div className="sidebar-widget related-posts-widget">
                <div className="widget-header">
                  <h3 className="widget-title">
                    <span className="widget-icon">📚</span>
                    相关文章
                  </h3>
                </div>
                <div className="widget-content">
                  <div className="related-posts-list">
                    {relatedPosts.map((relatedPost, index) => (
                      <Link 
                        key={relatedPost.id}
                        to={`/post/${relatedPost.id}`}
                        className="related-post-item"
                      >
                        <div className="related-post-image">
                          <img 
                            src={relatedPost.coverImage} 
                            alt={relatedPost.title}
                          />
                        </div>
                        <div className="related-post-content">
                          <h4 className="related-post-title">{relatedPost.title}</h4>
                          <div className="related-post-meta">
                            <span className="related-post-category">
                              {getCatEmoji(relatedPost.category)} {relatedPost.category}
                            </span>
                            <span className="related-post-date">
                              {formatDate(relatedPost.publishDate)}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 分享小部件 */}
            <div className="sidebar-widget share-widget">
              <div className="widget-header">
                <h3 className="widget-title">
                  <span className="widget-icon">📤</span>
                  分享文章
                </h3>
              </div>
              <div className="widget-content">
                <div className="share-buttons">
                  <button className="share-button wechat">
                    <span className="share-icon">💬</span>
                    微信
                  </button>
                  <button className="share-button weibo">
                    <span className="share-icon">🐱</span>
                    微博
                  </button>
                  <button className="share-button qq">
                    <span className="share-icon">🐧</span>
                    QQ
                  </button>
                  <button className="share-button link" onClick={handleShare}>
                    <span className="share-icon">🔗</span>
                    复制链接
                  </button>
                </div>
              </div>
            </div>

            {/* 猫咪进度 */}
            <div className="sidebar-widget cat-progress-widget">
              <div className="widget-header">
                <h3 className="widget-title">
                  <span className="widget-icon">📊</span>
                  阅读进度
                </h3>
              </div>
              <div className="widget-content">
                <div className="progress-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${readingProgress}%` }}
                    ></div>
                  </div>
                  <div className="progress-info">
                    <span className="progress-percent">{Math.round(readingProgress)}%</span>
                    <span className="progress-cat">{catMood}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 猫咪小助手 */}
            <div className="sidebar-widget cat-assistant-widget">
              <div className="widget-header">
                <h3 className="widget-title">
                  <span className="widget-icon">🐾</span>
                  猫咪小助手
                </h3>
              </div>
              <div className="widget-content">
                <div className="assistant-message">
                  <div className="assistant-cat">😸</div>
                  <p className="assistant-text">
                    阅读时记得给你的猫咪一些零食哦！
                  </p>
                </div>
                <button className="assistant-action">
                  <span className="action-icon">🐟</span>
                  喂食猫咪
                </button>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* 评论区 */}
      <section className="comments-section">
        <div className="comments-container">
          <div className="comments-header">
            <h2 className="comments-title">
              <span className="comments-icon">💬</span>
              猫咪讨论区 ({comments.length})
            </h2>
            <p className="comments-subtitle">分享你的想法，与其他猫友交流</p>
          </div>

          {/* 发表评论表单 */}
          <form className="comment-form" onSubmit={handleCommentSubmit}>
            <div className="form-header">
              <h3 className="form-title">
                <span className="form-icon">✏️</span>
                发表评论
              </h3>
              <div className="form-tips">
                <span className="tip-item">😺 文明讨论</span>
                <span className="tip-item">🐱 尊重他人</span>
                <span className="tip-item">🐾 分享经验</span>
              </div>
            </div>
            <div className="form-body">
              <textarea
                className="comment-input"
                placeholder="说点什么吧...（猫咪表示想听你的想法）"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={4}
              />
              <div className="form-actions">
                <div className="emoji-options">
                  <button 
                    type="button" 
                    className="emoji-btn"
                    onClick={() => setNewComment(prev => prev + '😺')}
                  >
                    😺
                  </button>
                  <button 
                    type="button" 
                    className="emoji-btn"
                    onClick={() => setNewComment(prev => prev + '😸')}
                  >
                    😸
                  </button>
                  <button 
                    type="button" 
                    className="emoji-btn"
                    onClick={() => setNewComment(prev => prev + '😻')}
                  >
                    😻
                  </button>
                  <button 
                    type="button" 
                    className="emoji-btn"
                    onClick={() => setNewComment(prev => prev + '🐱')}
                  >
                    🐱
                  </button>
                </div>
                <button 
                  type="submit" 
                  className="submit-comment-btn"
                  disabled={!newComment.trim()}
                >
                  <span className="submit-icon">📝</span>
                  发布评论
                </button>
              </div>
            </div>
          </form>

          {/* 评论列表 */}
          <div className="comments-list">
            {comments.length > 0 ? (
              comments.map((comment, index) => (
                <div 
                  key={comment.id || index} 
                  className="comment-item"
                  style={{ '--delay': index * 0.1 + 's' }}
                >
                  <div className="comment-avatar">
                    <img 
                      src={comment.user?.avatar || 'https://images.unsplash.com/photo-1533738363-b7f9aef128ce?w=50&h=50&fit=crop&crop=face'} 
                      alt={comment.user?.name || '用户'}
                    />
                    <div className="comment-avatar-badge"></div>
                  </div>
                  <div className="comment-content">
                    <div className="comment-header">
                      <div className="comment-author">
                        <h4 className="author-name">{comment.user?.name || '匿名用户'}</h4>
                        <span className="comment-date">{comment.date || '刚刚'}</span>
                      </div>
                      <div className="comment-actions">
                        <button 
                          className="comment-action like-action"
                          onClick={() => handleCommentLike(comment.id)}
                        >
                          <span className="action-icon">❤️</span>
                          <span className="action-count">{comment.likes || 0}</span>
                        </button>
                        <button className="comment-action reply-action">
                          <span className="action-icon">↪️</span>
                          回复
                        </button>
                      </div>
                    </div>
                    <div className="comment-text">{comment.content}</div>
                    <div className="comment-footer">
                      <span className="comment-cat-tag">
                        <span className="cat-tag-icon">🐾</span>
                        猫友评论
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-comments">
                <div className="no-comments-illustration">
                  <div className="no-comments-cat">😿</div>
                  <div className="no-comments-bubble">
                    "还没有评论呢..."
                  </div>
                </div>
                <h3 className="no-comments-title">暂时没有评论</h3>
                <p className="no-comments-text">成为第一个评论的猫友吧！</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 相关文章区域 */}
      {relatedPosts.length > 0 && (
        <section className="related-articles-section">
          <div className="related-articles-container">
            <div className="section-header">
              <h2 className="section-title">
                <span className="section-icon">📖</span>
                更多猫咪好文
              </h2>
              <p className="section-subtitle">你可能还会喜欢这些文章</p>
            </div>
            <div className="related-articles-grid">
              {relatedPosts.map((relatedPost, index) => (
                <Link 
                  key={relatedPost.id}
                  to={`/post/${relatedPost.id}`}
                  className="related-article-card"
                  style={{ '--delay': index * 0.1 + 's' }}
                >
                  <div className="article-image">
                    <img 
                      src={relatedPost.coverImage} 
                      alt={relatedPost.title}
                    />
                    <div className="image-overlay">
                      <span className="category-badge">
                        {getCatEmoji(relatedPost.category)}
                      </span>
                    </div>
                  </div>
                  <div className="article-content">
                    <h3 className="article-title">{relatedPost.title}</h3>
                    <p className="article-excerpt">{relatedPost.excerpt}</p>
                    <div className="article-meta">
                      <span className="meta-item">
                        <span className="meta-icon">👤</span>
                        {relatedPost.author?.name}
                      </span>
                      <span className="meta-item">
                        <span className="meta-icon">📅</span>
                        {formatDate(relatedPost.publishDate)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 底部导航 */}
      <div className="post-navigation">
        <div className="navigation-container">
          <button 
            className="nav-button prev-button"
            onClick={() => navigate(-1)}
          >
            <span className="nav-icon">←</span>
            <span className="nav-text">返回</span>
          </button>
          <div className="nav-center">
            <button 
              className="nav-button top-button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <span className="nav-icon">🐱</span>
              <span className="nav-text">回到顶部</span>
            </button>
          </div>
          <Link 
            to="/archive"
            className="nav-button next-button"
          >
            <span className="nav-text">更多文章</span>
            <span className="nav-icon">→</span>
          </Link>
        </div>
      </div>

      {/* 浮动猫咪助手 */}
      <div className="floating-cat-assistant">
        <button 
          className="floating-btn like-floating"
          onClick={handleLike}
        >
          <span className="floating-icon">
            {isLiked ? '❤️' : '🤍'}
          </span>
        </button>
        <button 
          className="floating-btn comment-floating"
          onClick={() => document.querySelector('.comments-section')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <span className="floating-icon">💬</span>
        </button>
        <button 
          className="floating-btn bookmark-floating"
          onClick={handleBookmark}
        >
          <span className="floating-icon">
            {isBookmarked ? '🔖' : '📑'}
          </span>
        </button>
      </div>

      {/* 猫咪尾巴分隔线 */}
      <div className="cat-tail-divider">
        <div className="tail-left"></div>
        <div className="tail-middle">🐱</div>
        <div className="tail-right"></div>
      </div>
    </div>
  );
};

export default PostDetail;